import {partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {AlignmentInput} from 'analyses/alignment/modals/createSequenceAlignmentsModalReducer';
import {DisabledOptionProps, getInputChip, inputToOptionData} from 'analyses/alignment/modals/SharedUtil';
import {NucleotideType} from 'biojs/BioConstants';
import {BulkAlignmentCreationKind} from 'constants/AlignmentConstants';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY, NEAR_BLACK} from 'ui-library/Colors';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {defaultRenderOptionContent} from 'ui-library/menus/MenuUtil';
import OptionContent from 'ui-library/menus/OptionContent';

// Exported for testing.
export enum SpecialOptionDataName {
  SELECT_ALL = 'Select all',
  UNSELECT_ALL = 'Unselect all',
  SELECT_ALL_DNA = 'Select all DNA',
  UNSELECT_ALL_DNA = 'Unselect all DNA',
  SELECT_ALL_RNA = 'Select all RNA',
  UNSELECT_ALL_RNA = 'Unselect all RNA',
  DNA_SEPARATOR = 'DNA',
  RNA_SEPARATOR = 'RNA',
}

type MappedSpecialOptionDatas = Record<SpecialOptionDataName, OptionData>;

const styles = StyleSheet.create({
  multiselectOption: {textAlign: 'center', borderBottom: `1px solid ${GRAY}`},
  separatorOption: {fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD, cursor: 'default', color: NEAR_BLACK},
});

const SPECIAL_OPTION_DATAS: MappedSpecialOptionDatas = {
  [SpecialOptionDataName.SELECT_ALL]: {
    className: css(styles.multiselectOption),
    dataTestAttrs: {component: 'AlignmentInputOption', element: 'root', key: 'selectAll'},
    name: SpecialOptionDataName.SELECT_ALL,
    value: SpecialOptionDataName.SELECT_ALL,
  },
  [SpecialOptionDataName.UNSELECT_ALL]: {
    className: css(styles.multiselectOption),
    dataTestAttrs: {component: 'AlignmentInputOption', element: 'root', key: 'unselectAll'},
    name: SpecialOptionDataName.UNSELECT_ALL,
    value: SpecialOptionDataName.UNSELECT_ALL,
  },
  [SpecialOptionDataName.SELECT_ALL_DNA]: {
    className: css(styles.multiselectOption),
    dataTestAttrs: {component: 'AlignmentInputOption', element: 'root', key: 'selectAllDNA'},
    name: SpecialOptionDataName.SELECT_ALL_DNA,
    value: SpecialOptionDataName.SELECT_ALL_DNA,
  },
  [SpecialOptionDataName.UNSELECT_ALL_DNA]: {
    className: css(styles.multiselectOption),
    dataTestAttrs: {component: 'AlignmentInputOption', element: 'root', key: 'unselectAllDNA'},
    name: SpecialOptionDataName.UNSELECT_ALL_DNA,
    value: SpecialOptionDataName.UNSELECT_ALL_DNA,
  },
  [SpecialOptionDataName.SELECT_ALL_RNA]: {
    className: css(styles.multiselectOption),
    dataTestAttrs: {component: 'AlignmentInputOption', element: 'root', key: 'selectAllRNA'},
    name: SpecialOptionDataName.SELECT_ALL_RNA,
    value: SpecialOptionDataName.SELECT_ALL_RNA,
  },
  [SpecialOptionDataName.UNSELECT_ALL_RNA]: {
    className: css(styles.multiselectOption),
    dataTestAttrs: {component: 'AlignmentInputOption', element: 'root', key: 'unselectAllRNA'},
    name: SpecialOptionDataName.UNSELECT_ALL_RNA,
    value: SpecialOptionDataName.UNSELECT_ALL_RNA,
  },
  [SpecialOptionDataName.DNA_SEPARATOR]: {
    className: css(styles.separatorOption),
    disabled: true,
    name: SpecialOptionDataName.DNA_SEPARATOR,
    value: SpecialOptionDataName.DNA_SEPARATOR,
  },
  [SpecialOptionDataName.RNA_SEPARATOR]: {
    className: css(styles.separatorOption),
    disabled: true,
    name: SpecialOptionDataName.RNA_SEPARATOR,
    value: SpecialOptionDataName.RNA_SEPARATOR,
  },
};

function filterChoicesByQuery(
  choices: Array<AlignmentInput>,
  query: string,
  getDisabledOptionProps: (option: AlignmentInput) => DisabledOptionProps
): Array<OptionData> {
  return choices
    .filter((input: AlignmentInput) => input.name.toLowerCase().includes(query.toLowerCase()))
    .map((input) => inputToOptionData(input, getDisabledOptionProps));
}

// Exported for testing.
export function buildOptionDatas({
  dnaChoices,
  rnaChoices,
  nonDisabledDNAChoices,
  nonDisabledRNAChoices,
  nonDisabledChoices,
  selection,
  query,
  alignmentKind,
  getDisabledOptionProps,
}: {
  dnaChoices: Array<AlignmentInput>;
  rnaChoices: Array<AlignmentInput>;
  nonDisabledDNAChoices: Array<AlignmentInput>;
  nonDisabledRNAChoices: Array<AlignmentInput>;
  nonDisabledChoices: Array<AlignmentInput>;
  selection: Array<AlignmentInput>;
  query: string;
  alignmentKind: BulkAlignmentCreationKind;
  getDisabledOptionProps: (option: AlignmentInput) => DisabledOptionProps;
}): Array<OptionData> {
  const optionDatas: Array<OptionData> = [];
  const dnaOptionDatas: Array<OptionData> = [];
  const rnaOptionDatas: Array<OptionData> = [];

  if (query !== '') {
    // If we're searching, only return non-disabled search results.
    dnaOptionDatas.push(...filterChoicesByQuery(nonDisabledDNAChoices, query, getDisabledOptionProps));
    rnaOptionDatas.push(...filterChoicesByQuery(nonDisabledRNAChoices, query, getDisabledOptionProps));
  } else {
    // If we don't have a search, show everything, even if it's disabled.
    dnaOptionDatas.push(...dnaChoices.map((input) => inputToOptionData(input, getDisabledOptionProps)));
    rnaOptionDatas.push(...rnaChoices.map((input) => inputToOptionData(input, getDisabledOptionProps)));

    // In consensus alignments, we want to show the 'right' kind of select/unselect all, depending on
    // whether we have all the available inputs selected or none of the available inputs selected.
    if (alignmentKind === BulkAlignmentCreationKind.CONSENSUS) {
      // If we only have one type of choices, show select/unselect all.
      if (dnaChoices.length === 0 || rnaChoices.length === 0) {
        if (selection.length === 0) {
          optionDatas.push(SPECIAL_OPTION_DATAS[SpecialOptionDataName.SELECT_ALL]);
        } else if (selection.length === nonDisabledChoices.length) {
          optionDatas.push(SPECIAL_OPTION_DATAS[SpecialOptionDataName.UNSELECT_ALL]);
        }
      } else {
        // If we have clickable DNA choices and any RNA choices, even if they're disabled,
        // show select/unselect DNA.
        if (nonDisabledDNAChoices.length > 0 && rnaChoices.length > 0) {
          if (selection.length === 0) {
            optionDatas.push(SPECIAL_OPTION_DATAS[SpecialOptionDataName.SELECT_ALL_DNA]);
          } else if (selection.length === nonDisabledDNAChoices.length) {
            optionDatas.push(SPECIAL_OPTION_DATAS[SpecialOptionDataName.UNSELECT_ALL_DNA]);
          }
        }
        // If we have clickable RNA choices and any DNA choices, even if they're disabled,
        // show select/unselect RNA.
        if (nonDisabledRNAChoices.length > 0 && dnaChoices.length > 0) {
          if (selection.length === 0) {
            optionDatas.push(SPECIAL_OPTION_DATAS[SpecialOptionDataName.SELECT_ALL_RNA]);
          } else if (selection.length === nonDisabledRNAChoices.length) {
            optionDatas.push(SPECIAL_OPTION_DATAS[SpecialOptionDataName.UNSELECT_ALL_RNA]);
          }
        }
      }
    } else {
      // For template alignments, just show select / unselect all.
      if (selection.length === 0) {
        optionDatas.push(SPECIAL_OPTION_DATAS[SpecialOptionDataName.SELECT_ALL]);
      }
      if (selection.length === nonDisabledChoices.length) {
        optionDatas.push(SPECIAL_OPTION_DATAS[SpecialOptionDataName.UNSELECT_ALL]);
      }
    }
  }

  if (dnaOptionDatas.length > 0 && rnaOptionDatas.length > 0) {
    dnaOptionDatas.unshift(SPECIAL_OPTION_DATAS[SpecialOptionDataName.DNA_SEPARATOR]);
    rnaOptionDatas.unshift(SPECIAL_OPTION_DATAS[SpecialOptionDataName.RNA_SEPARATOR]);
  }

  optionDatas.push(...dnaOptionDatas, ...rnaOptionDatas);

  return optionDatas;
}

interface AlignmentInputSelectorProps {
  choiceValues: Array<AlignmentInput>;
  selection: Array<AlignmentInput>;
  onSelect: (val: AlignmentInput) => void;
  getDisabledOptionProps: (option: AlignmentInput) => DisabledOptionProps;
  autofocus: boolean;
  alignmentKind: BulkAlignmentCreationKind;
}

export function AlignmentInputSelector({
  choiceValues,
  selection,
  onSelect,
  getDisabledOptionProps,
  autofocus,
  alignmentKind,
}: AlignmentInputSelectorProps): JSX.Element {
  const [query, setQuery] = useState('');

  const [dnaChoices, rnaChoices] = partition(
    choiceValues,
    (input) => input.nucleotideType === NucleotideType.DNA
  );

  const enabledChoices = choiceValues.filter((input) => !getDisabledOptionProps(input).disabled);

  const [enabledDNAChoices, enabledRNAChoices] = partition(
    enabledChoices,
    (input) => input.nucleotideType === NucleotideType.DNA
  );

  function modifiedOnSelect(value: AlignmentInput | SpecialOptionDataName): void {
    switch (value) {
      case SpecialOptionDataName.SELECT_ALL:
        for (const input of enabledChoices) {
          onSelect(input);
        }
        break;
      case SpecialOptionDataName.SELECT_ALL_DNA:
        for (const input of enabledDNAChoices) {
          onSelect(input);
        }
        break;
      case SpecialOptionDataName.SELECT_ALL_RNA:
        for (const input of enabledRNAChoices) {
          onSelect(input);
        }
        break;
      case SpecialOptionDataName.UNSELECT_ALL:
      case SpecialOptionDataName.UNSELECT_ALL_DNA:
      case SpecialOptionDataName.UNSELECT_ALL_RNA:
        for (const input of selection) {
          onSelect(input);
        }
        break;
      default:
        onSelect(value as AlignmentInput);
    }
  }

  function renderOptionContent(optionData: OptionData): JSX.Element {
    if ((Object.values(SpecialOptionDataName) as Array<string>).includes(optionData.value as string)) {
      return (
        <OptionContent className={optionData.className} dataTestAttrs={optionData.dataTestAttrs}>
          {optionData.name}
        </OptionContent>
      );
    } else {
      return defaultRenderOptionContent(optionData);
    }
  }

  return (
    <ChipSelectInput
      isMulti={true}
      autoFocus={autofocus}
      placeholder="Search"
      optionDatas={buildOptionDatas({
        dnaChoices,
        rnaChoices,
        nonDisabledDNAChoices: enabledDNAChoices,
        nonDisabledRNAChoices: enabledRNAChoices,
        nonDisabledChoices: enabledChoices,
        selection,
        query,
        alignmentKind,
        getDisabledOptionProps,
      })}
      value={selection}
      onSelect={modifiedOnSelect}
      renderChips={(inputs) => (
        <>
          {inputs.map((input) =>
            getInputChip(input, {
              size: ChipSize.MINI,
              onRemove: () => {
                onSelect(input);
              },
            })
          )}
        </>
      )}
      scrollable={true}
      query={query}
      onQueryChange={(e) => {
        setQuery(e.target.value);
      }}
      renderOptionContent={renderOptionContent}
    />
  );
}
